import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Store,
  Search,
  Plus,
  Edit,
  Trash2,
  User,
  Mail,
  Award,
  BarChart2,
  FileText,
  Percent,
  Eye,
  History,
  RotateCcw,
  XCircle,
  Clock,
  ShoppingBag,
  RefreshCw,
  Download, // Export
  Upload,   // Import
  CheckCircle,
  FileSpreadsheet,
  FileWarning,
  ArrowLeft,
  ChevronRight,
  Save,
  Check,
  X,
  Phone,
  ShieldCheck,
  Filter,
  MapPin,
  Building2,
  Briefcase
} from "lucide-react";
import { format } from "date-fns";

import Loader from "../../../loader/Loader";
import AddVendor from "../VendorModule/AddVendor";
import ViewVendor from "../VendorModule/ViewVendor";
import AuditInfoModal, { buildAuditRows } from "../../../shared/AuditInfoModal";
import Pagination from "../../Pagination";
import { domain, domainAccounting } from "../../../../security";
import { selectUserID } from "../../../../redux/IchthusSlice";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

const PRICE_TYPE_MAP = {
  "VAT Exclusive": "vatEx",
  "VAT Inclusive": "vatInc",
  "Reseller": "reseller",
  "Zero Rated": "zeroRated",
  // Fallbacks if user left system keys:
  vatEx: "vatEx",
  vatInc: "vatInc",
  reseller: "reseller",
  zeroRated: "zeroRated",
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return format(new Date(dateString), "MMM dd, yyyy");
};

// ==========================================
// SYNC CONFIRMATION MODAL
// ==========================================
const SyncConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  vendorCount,
  syncing,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col transform transition-all">
        {/* Header */}
        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-white">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <div className="p-2 bg-emerald-50 rounded-full text-emerald-600">
              <RefreshCw size={18} className={syncing ? "animate-spin" : ""} />
            </div>
            Sync to Accounting
          </h2>
          <button
            onClick={onClose}
            disabled={syncing}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition disabled:opacity-50"
          >
            <XCircle size={22} />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 bg-slate-50/50">
          <div className="bg-white p-4 rounded-xl border border-slate-200 mb-2">
            <p className="text-sm text-slate-600 leading-relaxed text-center">
              Are you sure you want to sync <br />
              <strong className="text-xl text-emerald-600">
                {vendorCount}
              </strong>{" "}
              <br />
              vendors to the Accounting system?
            </p>
            <p className="text-xs text-slate-400 text-center mt-3">
              This will securely push all current vendor data to the centralized
              accounting database.
            </p>
          </div>

          {/* Footer Actions */}
          <div className="flex justify-end gap-3 pt-5">
            <button
              onClick={onClose}
              disabled={syncing}
              className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={syncing}
              className="px-4 py-2 flex items-center gap-2 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors shadow-sm disabled:opacity-70"
            >
              {syncing ? (
                <>
                  <RefreshCw size={16} className="animate-spin" /> Syncing...
                </>
              ) : (
                "Confirm Sync"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// HISTORY MODAL
// ==========================================
const DeletedHistoryModal = ({ isOpen, onClose, onRestore }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [auditItem, setAuditItem] = useState(null);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${domain}/api/Vendors/history?search=${search}`,
      );
      setHistoryData(response.data);
    } catch {
      toast.error("Failed to load vendor history.");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
      setCurrentPage(1);
    }
  }, [isOpen, fetchHistory]);

  if (!isOpen) return null;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = historyData.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-white">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <div className="p-2 bg-red-50 rounded-full text-red-500">
              <Trash2 size={18} />
            </div>
            Deleted Vendor History
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition"
          >
            <XCircle size={22} />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 bg-slate-50/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search deleted vendors..."
              className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-red-100 focus:border-red-400 text-sm outline-none transition-all"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 bg-slate-50/50">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader />
            </div>
          ) : historyData.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 mb-3">
                <History size={20} className="text-slate-400" />
              </div>
              <p className="text-slate-500 text-sm">
                No deleted vendors found.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {/* Desktop Table */}
              <div className="hidden sm:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full text-sm text-left text-slate-600">
                  <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                    <tr>
                      <th className="px-4 py-3">Vendor Name</th>
                      <th className="px-4 py-3">Code</th>
                      <th className="px-4 py-3">Deleted By</th>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {currentItems.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-800 flex items-center gap-1">
                          {item.vendorName}
                          <button
                            onClick={() => setAuditItem(item)}
                            className="text-amber-500 hover:text-amber-700 ml-1"
                            title="View Audit Info"
                          >
                            <Clock size={14} />
                          </button>
                        </td>
                        <td className="px-4 py-3 text-xs font-mono text-slate-500">
                          {item.vendorCode}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          <span className="font-medium text-red-500">
                            {item.deletedByFullName || "Unknown"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-red-500 text-xs">
                          {formatDate(item.deletedDate)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={async () => {
                              await onRestore(item.id);
                              fetchHistory();
                            }}
                            className="text-green-600 hover:text-green-700 font-semibold text-xs flex items-center justify-end gap-1 ml-auto"
                          >
                            <RotateCcw size={14} /> Restore
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Stack */}
              <div className="sm:hidden flex flex-col gap-3">
                {currentItems.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="font-semibold text-slate-800">
                          {item.vendorName}
                        </h4>
                        <span className="text-[10px] font-mono text-slate-400">
                          {item.vendorCode}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setAuditItem(item)}
                          className="p-2 bg-amber-50 text-amber-500 rounded-lg hover:bg-amber-100 transition"
                        >
                          <Clock size={16} />
                        </button>
                        <button
                          onClick={async () => {
                            await onRestore(item.id);
                            fetchHistory();
                          }}
                          className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition"
                        >
                          <RotateCcw size={18} />
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Trash2 size={10} className="text-red-400" />
                        Deleted by:{" "}
                        <span className="font-medium text-red-500">
                          {item.deletedByFullName || "Unknown"}
                        </span>
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={10} className="text-slate-400" />
                        {formatDate(item.deletedDate)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-2">
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={historyData.length}
                  currentPage={currentPage}
                  paginate={(p) => setCurrentPage(p)}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={auditItem?.vendorName}
        rows={buildAuditRows(auditItem)}
      />
    </div>
  );
};

// ==========================================
// MOBILE CARD
// ==========================================
const VendorCard = ({ vendor, onView, onEdit, onDelete, onAudit }) => (
  <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200">
    <div className="flex justify-between items-start mb-3">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
          <Store size={18} />
        </div>
        <div>
          <span className="font-bold text-slate-800 text-lg block">
            {vendor.vendorName}
          </span>
          <div className="text-[10px] text-slate-400 font-mono">
            {vendor.vendorCode}
          </div>
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
              vendor.vendorStatus === "Active"
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {vendor.vendorStatus}
          </span>
        </div>
      </div>
    </div>

    <div className="space-y-2 text-sm text-slate-600 mb-4">
      <div className="flex items-center gap-2">
        <User size={14} className="text-slate-400" />
        <span className="font-medium">Contact:</span>{" "}
        {vendor.contactPerson || "N/A"}
      </div>
      <div className="flex items-center gap-2">
        <Mail size={14} className="text-slate-400" />
        <span className="truncate">{vendor.email || "N/A"}</span>
      </div>
      <div className="flex items-center gap-2">
        <Award size={14} className="text-slate-400" />
        <span>{vendor.specialization || "General"}</span>
      </div>
      <div className="flex items-center gap-2">
        <FileText size={14} className="text-slate-400" />
        <span>TIN: {vendor.tin || "N/A"}</span>
      </div>
      <div className="flex items-center gap-2">
        <Percent size={14} className="text-slate-400" />
        <span>EWT: </span>
        <span
          className={`px-2 py-0.5 rounded text-xs font-semibold ${
            vendor.ewt === true
              ? "bg-orange-100 text-orange-700"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          {vendor.ewt ? "Yes" : "No"}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <ShoppingBag size={14} className="text-slate-400" />
        <span>Non-Trade: </span>
        <span
          className={`px-2 py-0.5 rounded text-xs font-semibold ${
            vendor.nonTrade === true
              ? "bg-purple-100 text-purple-700"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          {vendor.nonTrade ? "Yes" : "No"}
        </span>
      </div>
      {/* Audit info */}
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <User size={10} />
        Created by:{" "}
        <span className="font-medium text-slate-600">
          {vendor.createdByFullName || "System"}
        </span>
        · {formatDate(vendor.createdDate)}
      </div>
    </div>

    <div className="flex gap-2 pt-3 border-t border-slate-100">
      <button
        onClick={() => onAudit(vendor)}
        className="flex-1 py-2 border border-amber-100 text-amber-600 rounded hover:bg-amber-50 text-xs font-medium flex justify-center items-center gap-1"
      >
        <Clock size={14} /> Audit
      </button>
      <button
        onClick={() => onView(vendor.id)}
        className="flex-1 py-2 border border-blue-100 text-blue-600 rounded hover:bg-blue-50 text-xs font-medium flex justify-center items-center gap-1"
      >
        <Eye size={14} /> View
      </button>
      <button
        onClick={() => onEdit(vendor)}
        className="flex-1 py-2 border border-indigo-100 text-indigo-600 rounded hover:bg-indigo-50 text-xs font-medium flex justify-center items-center gap-1"
      >
        <Edit size={14} /> Edit
      </button>
      <button
        onClick={() => onDelete(vendor.id)}
        className="flex-1 py-2 border border-red-100 text-red-600 rounded hover:bg-red-50 text-xs font-medium flex justify-center items-center gap-1"
      >
        <Trash2 size={14} /> Delete
      </button>
    </div>
  </div>
);

// ==========================================
// MAIN COMPONENT
// ==========================================
const AllVendors = () => {
  const currentUserID = useSelector(selectUserID);

  const [vendorData, setVendorData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [vendorToEdit, setVendorToEdit] = useState(null);

  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [vendorToViewId, setVendorToViewId] = useState(null);

  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [auditItem, setAuditItem] = useState(null);

  // Sync state
  const [isSyncModalVisible, setIsSyncModalVisible] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [vendorsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredVendors, setFilteredVendors] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${domain}/api/Vendors`);
      setVendorData(response.data);
    } catch {
      toast.error("Failed to fetch vendors.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const lower = searchTerm.toLowerCase();
    const results = vendorData.filter(
      (v) =>
        (v.vendorName || "").toLowerCase().includes(lower) ||
        (v.contactPerson || "").toLowerCase().includes(lower) ||
        (v.email || "").toLowerCase().includes(lower) ||
        (v.tin || "").toLowerCase().includes(lower),
    );
    setFilteredVendors(results);
    setCurrentPage(1);
  }, [searchTerm, vendorData]);

  const handleSoftDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this vendor?")) return;
    try {
      await axios.put(
        `${domain}/api/Vendors/soft-delete/${id}`,
        { userID: currentUserID },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success("Vendor moved to history!");
      fetchData();
    } catch {
      toast.error("Failed to delete vendor.");
    }
  };

  const handleRestore = async (id) => {
    try {
      await axios.put(
        `${domain}/api/Vendors/restore/${id}`,
        { userID: currentUserID },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success("Vendor restored successfully!");
      fetchData();
    } catch {
      toast.error("Failed to restore vendor.");
    }
  };

  // ==========================================
  // SYNC ACTION LOGIC
  // ==========================================
  const handleSyncToAccounting = async () => {
    if (!vendorData || vendorData.length === 0) {
      toast.error("No vendors available to sync.");
      return;
    }

    try {
      setIsSyncing(true);

      // Mapping data to fit Accounting Domain API model
      const payload = vendorData.map((v) => {
        const numericUserId = currentUserID ? parseInt(currentUserID, 10) : 0;

        return {
          vendorId: v.vendorCode || v.id?.toString() || "",
          name1: v.vendorName || "",
          name2: "",
          street: v.address || v.street || "",
          cityId: v.cityId || "",
          districtId: v.districtId || "",
          countryId: v.countryId || "",
          regionId: v.regionId || "",
          zip: v.zipCode || v.zip || "",
          mobile: v.mobile || v.contactNumber || "",
          telephone: v.telephone || "",
          tin: v.tin || "",
          fax: v.fax || "",
          email: v.email || "",
          searchTerm: v.contactPerson || "",
          isEWT: !!v.ewt,
          priceType: v.priceType === "vatEx" ? "Exclusive" :
              v.priceType === "vatInc" ?  "Inclusive" :
              v.priceType === "reseller" ?  "Reseller" :
              v.priceType === "zeroRated" ?  "Zero Rated" :
              "",
          specialization: v.specialization || "",
          terms: v.termsDescription,
          certificateOfRegistration: v.certificateOfRegistration || "",
          createdDate: v.createdDate || new Date().toISOString(),
          createdBy: isNaN(numericUserId) ? 0 : numericUserId,
        };
      });

      // --- LOG FITS BEFORE PASSING ---
      // console.log(`=== VENDOR SYNC PAYLOAD ===`);
      // console.log(JSON.stringify(payload, null, 2));
      // console.log("===========================");

      const endpoint = `${domainAccounting}/api/Vendor/bulk`;

      await axios.post(endpoint, payload, {
        headers: { "Content-Type": "application/json" },
      });

      toast.success(
        `Successfully synced ${payload.length} vendors to Accounting!`,
      );
      setIsSyncModalVisible(false);
    } catch (error) {
      console.error("Accounting Sync Error:", error);
      toast.error("Failed to sync vendors to Accounting.");
    } finally {
      setIsSyncing(false);
    }
  };

  const openModal = (vendor = null) => {
    setVendorToEdit(vendor);
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setVendorToEdit(null);
    fetchData();
  };

  const openViewModal = (id) => {
    setVendorToViewId(id);
    setIsViewModalVisible(true);
  };

  const closeViewModal = () => {
    setIsViewModalVisible(false);
    setVendorToViewId(null);
  };

  const indexOfLastVendor = currentPage * vendorsPerPage;
  const indexOfFirstVendor = indexOfLastVendor - vendorsPerPage;
  const currentVendors = filteredVendors.slice(
    indexOfFirstVendor,
    indexOfLastVendor,
  );

  const fetchTermsData = useCallback(async () => {
    try {
      const response = await axios.get(`${domain}/api/Terms` , {
          headers: { 'Content-Type': 'application/json'}
      })
        
      return (response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
      return []
    }
  }, []);

  const priceTypes = [
    {value: "vatEx", name: "VAT Exclusive"},
    {value: "vatInc", name: "VAT Inclusive"},
    {value: "reseller", name: "Reseller"},
    {value: "zeroRated", name: "Zero Rated"},
  ]

  const exportHandleClick = async () => {
    if (!vendorData?.length) return;

    setLoading(true);

    try {
      const terms = (await fetchTermsData()) || [];

      // Extract list of term descriptions
      const termsList = terms
        .map((t) => (typeof t === "string" ? t : t?.description))
        .filter(Boolean);

      const priceTypeListString = `"${priceTypes.map((p) => p.name).join(",")}"`;

      const workbook = new ExcelJS.Workbook();

      // ==========================================
      // TAB: VENDORS
      // ==========================================
      const vendorSheet = workbook.addWorksheet("Vendors", {
        views: [{ showGridLines: true }],
      });

      // Column Map:
      // A: VendorCode (1)
      // B: VendorName (2)
      // C: VendorStatus (3)
      // D: Address (4)
      // E: ContactPerson (5)
      // F: Email (6)
      // G: PhoneNumber (7)
      // H: TIN (8)
      // I: NonTrade (9)
      // J: Specialization (10)
      // K: PriceType (11)
      // L: EWT (12)
      // M: TermsDescription (13)
      vendorSheet.columns = [
        { header: "Vendor Code", key: "VendorCode" },
        { header: "Vendor Name", key: "VendorName" },
        { header: "Status", key: "VendorStatus" },
        { header: "Address", key: "Address" },
        { header: "Contact Person", key: "ContactPerson" },
        { header: "Email", key: "Email" },
        { header: "Phone Number", key: "PhoneNumber" },
        { header: "TIN", key: "TIN" },
        { header: "Non-Trade", key: "NonTrade" },
        { header: "Specialization", key: "Specialization" },
        { header: "Price Type", key: "PriceType" },
        { header: "EWT", key: "EWT" },
        { header: "Terms Description", key: "TermsDescription" },
      ];

      // Alignments for boolean/status columns
      vendorSheet.getColumn("VendorStatus").alignment = { horizontal: "center" };
      vendorSheet.getColumn("NonTrade").alignment = { horizontal: "center" };
      vendorSheet.getColumn("EWT").alignment = { horizontal: "center" };

      // Store Terms in a hidden lookup sheet to bypass Excel's 255-character limit
      if (termsList.length > 0) {
        const lookupSheet = workbook.addWorksheet("Lists");
        lookupSheet.state = "hidden"; // Itago ang reference sheet
        termsList.forEach((desc, idx) => {
          lookupSheet.getCell(`A${idx + 1}`).value = desc;
        });
      }

      // Add Vendor Data Rows
      vendorData.forEach((i) => {
        // 1. Map Price Type (value -> display name)
        const matchedPriceType = priceTypes.find(
          (pt) => pt.value === i.priceType || pt.name === i.priceType
        );
        const priceTypeName = matchedPriceType
          ? matchedPriceType.name
          : i.priceType ?? "";

        // 2. Map Terms description
        const matchedTerm = terms.find(
          (t) => t.id === i.termsId || t.description === i.termsDescription
        );
        const termsDescription =
          matchedTerm?.description ?? i.termsDescription ?? "";

        vendorSheet.addRow({
          VendorCode: i.vendorCode ?? "",
          VendorName: i.vendorName ?? "",
          VendorStatus: i.vendorStatus ?? "Active",
          Address: i.address ?? "",
          ContactPerson: i.contactPerson ?? "",
          Email: i.email ?? "",
          PhoneNumber: i.phoneNumber ?? "",
          TIN: i.tin ?? "",
          NonTrade: i.nonTrade ? "Yes" : "No",
          Specialization: i.specialization ?? "",
          PriceType: priceTypeName,
          EWT: i.ewt ? "Yes" : "No",
          TermsDescription: termsDescription,
        });
      });

      // =========================================================================
      // RANGE-WIDE DROPDOWNS (Entire column: row 2 to 9999)
      // Kahit mag-type ka ng bagong vendor sa Excel, may dropdown na agad
      // =========================================================================

      // Column K: Price Type
      vendorSheet.dataValidations.add("K2:K9999", {
        type: "list",
        allowBlank: true,
        formulae: [priceTypeListString],
        showErrorMessage: true,
        errorTitle: "Invalid Price Type",
        error: "Please select a valid Price Type from the dropdown.",
      });

      // Column M: Terms Description
      if (termsList.length > 0) {
        vendorSheet.dataValidations.add("M2:M9999", {
          type: "list",
          allowBlank: true,
          formulae: [`Lists!$A$1:$A$${termsList.length}`],
          showErrorMessage: true,
          errorTitle: "Invalid Term",
          error: "Please select a valid Term Description from the dropdown.",
        });
      }

      // ==========================================
      // STYLING & COLUMN MIN-WIDTH
      // ==========================================
      const applySheetFormatting = (sheet, minWidth = 16) => {
        const headerRow = sheet.getRow(1);
        headerRow.height = 26;
        headerRow.eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFF" }, size: 10 };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "1E40AF" }, // Navy Blue
          };
          cell.alignment = { vertical: "middle", horizontal: "center" };
        });

        sheet.columns.forEach((col) => {
          let maxLen = col.header ? col.header.toString().length : 10;
          col.eachCell({ includeEmpty: false }, (cell) => {
            const valStr = cell.value ? cell.value.toString() : "";
            if (valStr.length > maxLen) maxLen = valStr.length;
          });
          col.width = Math.max(maxLen + 4, minWidth);
        });
      };

      applySheetFormatting(vendorSheet, 16);

      // ==========================================
      // EXPORT FILE
      // ==========================================
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `Vendor_Export_${new Date().getTime()}.xlsx`);
    } catch (error) {
      console.error("Export Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const [showUploadForm, setShowUploadForm] = useState(false);
  const openUploadForm = () => {
    setShowUploadForm(true)
  }
  const closeUploadForm = () => {
    setShowUploadForm(false)
  }

  return (
    <div className="min-h-screen pb-12">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-20">
        <div className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Store className="text-indigo-600" /> Vendor Management
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Manage hardware suppliers, contact details, and tax info.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 w-full md:w-auto">
              {/* Sync Button */}
              <button
                onClick={() => setIsSyncModalVisible(true)}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-600 hover:bg-emerald-100 text-sm font-medium px-4 py-2.5 rounded-xl transition-all"
              >
                <RefreshCw size={18} />
                <span>Sync to Accounting</span>
              </button>

              <button
                onClick={() => setIsHistoryModalVisible(true)}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100 text-sm font-medium px-4 py-2.5 rounded-xl transition-all"
              >
                <History size={18} />
                <span>History</span>
              </button>
              <button
                onClick={() => openModal()}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-5 py-2.5 rounded-xl shadow-md shadow-indigo-200 transition-all"
              >
                <Plus size={18} />
                Add Vendor
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {/* Stats & Search */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4">
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
              <BarChart2 size={24} />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Active Vendors
              </p>
              <p className="text-2xl font-bold text-slate-800">
                {filteredVendors.length}
              </p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search vendor name, contact, email, TIN..."
                className="w-full pl-10 pr-4 py-2 text-sm border-none ring-1 ring-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all placeholder:text-slate-400"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 w-full md:w-auto py-2">
          {/* Export */}
          <button
            onClick={exportHandleClick}
            className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-600 hover:bg-emerald-100 text-sm font-medium px-4 py-2.5 rounded-xl transition-all"
          >
            <Download size={18} />
            <span>Export</span>
          </button>

          {/* Import */}
          <button
            onClick={openUploadForm}
            className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 text-sm font-medium px-4 py-2.5 rounded-xl transition-all"
          >
            <Upload size={18} />
            <span>Import</span>
          </button>
        </div>


        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader />
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <table className="w-full text-sm text-left text-slate-600">
                <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 font-semibold">Vendor</th>
                    <th className="px-6 py-4 font-semibold">Contact</th>
                    <th className="px-6 py-4 font-semibold">TIN</th>
                    <th className="px-6 py-4 font-semibold text-center">EWT</th>
                    <th className="px-6 py-4 font-semibold text-center">
                      Non-Trade
                    </th>
                    <th className="px-6 py-4 font-semibold">Status</th>
                    <th className="px-6 py-4 font-semibold">Created Info</th>
                    <th className="px-6 py-4 font-semibold text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {currentVendors.map((vendor) => (
                    <tr
                      key={vendor.id}
                      className="hover:bg-slate-50 transition"
                    >
                      <td className="px-6 py-4">
                        <div className="font-bold text-slate-800">
                          {vendor.vendorName}
                        </div>
                        <div className="text-[10px] font-mono text-slate-400">
                          {vendor.vendorCode}
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5">
                          {vendor.specialization}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <User size={12} className="text-slate-400" />
                            {vendor.contactPerson}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-400">
                            <Mail size={12} className="text-slate-400" />
                            {vendor.email}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 font-mono text-slate-700">
                        {vendor.tin || "-"}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${
                            vendor.ewt === true
                              ? "bg-orange-100 text-orange-700"
                              : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {vendor.ewt ? "Yes" : "No"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${
                            vendor.nonTrade === true
                              ? "bg-purple-100 text-purple-700"
                              : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {vendor.nonTrade ? "Yes" : "No"}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            vendor.vendorStatus === "Active"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {vendor.vendorStatus}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs">
                        <div className="flex flex-col">
                          <span className="font-semibold text-slate-700">
                            {vendor.createdByFullName || "System"}
                          </span>
                          <span className="text-slate-400">
                            {formatDate(vendor.createdDate)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setAuditItem(vendor)}
                            className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                            title="View Audit Info"
                          >
                            <Clock size={18} />
                          </button>
                          <button
                            onClick={() => openViewModal(vendor.id)}
                            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            onClick={() => openModal(vendor)}
                            className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleSoftDelete(vendor.id)}
                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {currentVendors.length === 0 && (
                    <tr>
                      <td
                        colSpan="8"
                        className="px-6 py-10 text-center text-slate-400"
                      >
                        No vendors found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="p-4 border-t border-slate-100 hidden md:block">
              <Pagination
                itemsPerPage={vendorsPerPage}
                totalItems={filteredVendors.length}
                currentPage={currentPage}
                paginate={(p) => setCurrentPage(p)}
              />
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden flex flex-col gap-3">
              {currentVendors.map((vendor) => (
                <VendorCard
                  key={vendor.id}
                  vendor={vendor}
                  onView={openViewModal}
                  onEdit={openModal}
                  onDelete={handleSoftDelete}
                  onAudit={setAuditItem}
                />
              ))}
              {currentVendors.length === 0 && (
                <div className="text-center py-10 text-slate-400 bg-white rounded-2xl border border-slate-100">
                  No vendors found.
                </div>
              )}
              <div className="mt-2 pb-6">
                <Pagination
                  itemsPerPage={vendorsPerPage}
                  totalItems={filteredVendors.length}
                  currentPage={currentPage}
                  paginate={(p) => setCurrentPage(p)}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Sync Confirm Modal */}
      <SyncConfirmModal
        isOpen={isSyncModalVisible}
        onClose={() => setIsSyncModalVisible(false)}
        onConfirm={handleSyncToAccounting}
        vendorCount={vendorData.length}
        syncing={isSyncing}
      />

      {/* Add/Edit Modal */}
      {isModalVisible && (
        <AddVendor
          onClose={closeModal}
          refreshData={fetchData}
          vendorToEdit={vendorToEdit}
        />
      )}

      {/* View Modal */}
      {isViewModalVisible && (
        <ViewVendor vendorId={vendorToViewId} onClose={closeViewModal} />
      )}

      {/* History Modal */}
      <DeletedHistoryModal
        isOpen={isHistoryModalVisible}
        onClose={() => {
          setIsHistoryModalVisible(false);
          fetchData();
        }}
        onRestore={handleRestore}
      />

      {/* Audit Modal */}
      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={auditItem?.vendorName}
        rows={buildAuditRows(auditItem)}
      />

      <UploadFormModal
        isOpen={showUploadForm}
        onClose={closeUploadForm}
        onUploadSuccess={fetchData}
      />
    </div>
  );
};

const UploadFormModal = ({ isOpen, onClose, onUploadSuccess }) => {
  // --- States ---
  const [fileData, setFileData] = useState([]);
  const [existingVendors, setExistingVendors] = useState([]);
  const [termsList, setTermsList] = useState([]);
  const [isSystemDataLoading, setIsSystemDataLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [step, setStep] = useState(1);
  const [note, setNote] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const fileInputRef = useRef(null);

  const userID = useSelector((state) => state.auth?.userID || state.user?.userID);
  const fullName = useSelector((state) => state.auth?.fullName || state.user?.fullName);

  // --- Effects ---
  useEffect(() => {
    if (isOpen) {
      fetchPrerequisites();
      setStep(1);
      setFileData([]);
      setValidationErrors([]);
      setNote("");
      setStatusFilter("ALL");
    }
  }, [isOpen]);

  const fetchPrerequisites = async () => {
    setIsSystemDataLoading(true);
    try {
      const [vendorRes, termsRes] = await Promise.all([
        axios.get(`${domain}/api/Vendors`).catch(() => ({ data: [] })),
        axios.get(`${domain}/api/Terms`).catch(() => ({ data: [] })),
      ]);
      setExistingVendors(vendorRes.data || []);
      setTermsList(termsRes.data || []);
    } catch (error) {
      console.error("Failed to fetch initial references", error);
      toast.warning("Could not sync existing vendors list.");
    } finally {
      setIsSystemDataLoading(false);
    }
  };

  // --- Normalization Helpers for Deep Comparison ---
  const normalizeStr = (val) => (val ?? "").toString().trim().toLowerCase();
  const normalizeBool = (val) => Boolean(val);

  // --- File Reader & Parser ---
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });

        // Find Vendors Sheet (or default to the first sheet)
        const vendorSheetName =
          wb.SheetNames.find((s) => s.toLowerCase().includes("vendor")) ||
          wb.SheetNames[0];

        const vendorWs = wb.Sheets[vendorSheetName];
        const rawVendorData = XLSX.utils.sheet_to_json(vendorWs, { raw: false });

        if (rawVendorData.length === 0) {
          toast.error("The uploaded file is empty.");
          return;
        }

        processAndValidate(rawVendorData);
      } catch (error) {
        console.error("Parse Error:", error);
        toast.error("Failed to parse the Excel file.");
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = "";
  };

  // --- Validation, Mapping & Deep Diff Detection ---
  const processAndValidate = (rawVendors) => {
    const errors = [];
    const vendorCodeSet = new Set();

    const formattedData = rawVendors.map((row, index) => {
      const rowNum = index + 2;

      // Extract values matching your Vendor export headers
      const vendorCode = (row["Vendor Code"] || row["VendorCode"] || "").toString().trim();
      const vendorName = (row["Vendor Name"] || row["VendorName"] || "").toString().trim();
      const vendorStatus = (row["Status"] || row["Vendor Status"] || row["vendorStatus"] || "Active").toString().trim();
      const address = (row["Address"] || "").toString().trim();
      const contactPerson = (row["Contact Person"] || row["ContactPerson"] || "").toString().trim();
      const email = (row["Email"] || "").toString().trim();
      const phoneNumber = (
        row["Phone Number"] ||
        row["PhoneNumber"] ||
        row["Mobile Number"] ||
        ""
      ).toString().trim();

      const tin = (
        row["TIN"] ||
        row["TIN Number"] ||
        row["tin"] ||
        ""
      ).toString().trim();

      const nonTradeRaw = (row["Non-Trade"] || row["NonTrade"] || "").toString().trim().toLowerCase();
      const nonTrade = nonTradeRaw === "yes" || nonTradeRaw === "true" || nonTradeRaw === "1";

      const specialization = (row["Specialization"] || "").toString().trim();

      const priceTypeRaw = (row["Price Type"] || row["PriceType"] || "").toString().trim();
      const priceType = PRICE_TYPE_MAP[priceTypeRaw] || priceTypeRaw || "vatInc";

      const ewtRaw = (row["EWT"] || "").toString().trim().toLowerCase();
      const ewt = ewtRaw === "yes" || ewtRaw === "true" || ewtRaw === "1";

      const termsDesc = (row["Terms Description"] || row["TermsDescription"] || "").toString().trim();

      // Find matching term ID
      const matchedTerm = termsList.find(
        (t) => normalizeStr(t.description) === normalizeStr(termsDesc)
      );

      // --- Validations ---
      if (!vendorCode) {
        errors.push(`Row ${rowNum}: 'Vendor Code' is required.`);
      } else if (vendorCodeSet.has(vendorCode.toLowerCase())) {
        errors.push(`Row ${rowNum}: Duplicate Vendor Code '${vendorCode}' found in file.`);
      } else {
        vendorCodeSet.add(vendorCode.toLowerCase());
      }

      if (!vendorName) {
        errors.push(`Row ${rowNum}: 'Vendor Name' is required.`);
      }

      // Check against existing system vendors
      const existing = existingVendors.find(
        (v) => normalizeStr(v.vendorCode) === normalizeStr(vendorCode)
      );

      // --- Change Detection ---
      let status = "NEW"; // NEW | UPDATED | UNCHANGED

      if (existing) {
        const updatedFields = [];

        const fieldsToCheck = [
          ["vendorName", existing.vendorName, vendorName],
          ["vendorStatus", existing.vendorStatus, vendorStatus],
          ["address", existing.address, address],
          ["contactPerson", existing.contactPerson, contactPerson],
          ["email", existing.email, email],
          ["phoneNumber", existing.phoneNumber, phoneNumber],
          ["tin", existing.tin, tin],
          ["specialization", existing.specialization, specialization],
          ["priceType", existing.priceType, priceType],
        ];

        fieldsToCheck.forEach(([field, oldValue, newValue]) => {
          if (normalizeStr(oldValue) !== normalizeStr(newValue)) {
            updatedFields.push({ field, oldValue, newValue });
          }
        });

        if (normalizeBool(existing.ewt) !== normalizeBool(ewt)) {
          updatedFields.push({ field: "ewt", oldValue: existing.ewt, newValue: ewt });
        }

        if (normalizeBool(existing.nonTrade) !== normalizeBool(nonTrade)) {
          updatedFields.push({ field: "nonTrade", oldValue: existing.nonTrade, newValue: nonTrade });
        }

        const oldTerm = matchedTerm?.id ? existing.termsId : existing.termsDescription;
        const newTerm = matchedTerm?.id ? matchedTerm.id : termsDesc;

        if (
          matchedTerm?.id
            ? oldTerm !== newTerm
            : normalizeStr(oldTerm) !== normalizeStr(newTerm)
        ) {
          updatedFields.push({
            field: matchedTerm?.id ? "termsId" : "termsDescription",
            oldValue: oldTerm,
            newValue: newTerm,
          });
        }

        status = updatedFields.length > 0 ? "UPDATED" : "UNCHANGED";
      }

      return {
        vendorCode,
        vendorName,
        vendorStatus,
        address,
        contactPerson,
        email,
        phoneNumber,
        tin,
        nonTrade,
        specialization,
        priceType,
        priceTypeDisplay: priceTypeRaw,
        ewt,
        termsDescription: termsDesc,
        termsId: matchedTerm?.id || null,
        status, // "NEW" | "UPDATED" | "UNCHANGED"
        existingId: existing?.id || null,
      };
    });

    setValidationErrors(errors);
    setFileData(formattedData);
    setStatusFilter("ALL");
    setStep(2);
  };

  // --- Save Handler ---
  const handleFinalSave = async () => {
    setIsProcessing(true);
    try {
      const payload = {
        vendors: fileData.map((item) => ({
          vendorCode: item.vendorCode,
          vendorName: item.vendorName,
          vendorStatus: item.vendorStatus,
          address: item.address,
          contactPerson: item.contactPerson,
          email: item.email,
          phoneNumber: item.phoneNumber,
          tin: item.tin,
          nonTrade: item.nonTrade,
          specialization: item.specialization,
          priceType: item.priceType,
          ewt: item.ewt,
          termsDescription: item.termsDescription,
          termsId: item.termsId,
          status: item.status,
        })),
        notes: note,
        uploadedBy: fullName,
        userId: userID,
      };

      await axios.post(`${domain}/api/Vendors/upload-bulk`, payload);
      toast.success("Vendors imported successfully!");

      if (onUploadSuccess) onUploadSuccess();
      handleClose();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to upload vendors.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setFileData([]);
    setStep(1);
    setStatusFilter("ALL");
    onClose();
  };

  // Status Counts
  const countNew = useMemo(() => fileData.filter((v) => v.status === "NEW").length, [fileData]);
  const countUpdated = useMemo(() => fileData.filter((v) => v.status === "UPDATED").length, [fileData]);
  const countUnchanged = useMemo(() => fileData.filter((v) => v.status === "UNCHANGED").length, [fileData]);

  // Filtered Rows for Table
  const filteredData = useMemo(() => {
    if (statusFilter === "ALL") return fileData;
    return fileData.filter((item) => item.status === statusFilter);
  }, [fileData, statusFilter]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4 transition-all">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col overflow-hidden border border-slate-200 font-sans">
        {/* --- Header --- */}
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-white z-20">
          <div className="flex items-center gap-3">
            <div
              className={`p-2 rounded-lg ${
                step === 1
                  ? "bg-indigo-100 text-indigo-600"
                  : "bg-emerald-100 text-emerald-600"
              }`}
            >
              {step === 1 ? <Upload size={20} /> : <CheckCircle size={20} />}
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-800">
                Bulk Vendor Upload
              </h3>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span
                  className={`h-2 w-2 rounded-full ${
                    step >= 1 ? "bg-indigo-500" : "bg-slate-300"
                  }`}
                />
                <span>Upload</span>
                <span className="text-slate-300">/</span>
                <span
                  className={`h-2 w-2 rounded-full ${
                    step >= 2 ? "bg-indigo-500" : "bg-slate-300"
                  }`}
                />
                <span>Review</span>
                <span className="text-slate-300">/</span>
                <span
                  className={`h-2 w-2 rounded-full ${
                    step >= 3 ? "bg-indigo-500" : "bg-slate-300"
                  }`}
                />
                <span>Confirm</span>
              </div>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* --- Body Container --- */}
        <div className="flex-1 relative overflow-hidden bg-slate-50/50">
          {/* STEP 1: UPLOAD */}
          {step === 1 && (
            <div className="absolute inset-0 flex items-center justify-center p-8 animate-in fade-in zoom-in-95">
              <div
                className={`w-full max-w-2xl border-2 border-dashed rounded-2xl p-12 flex flex-col items-center justify-center text-center transition-all cursor-pointer bg-white ${
                  isSystemDataLoading
                    ? "border-slate-200 bg-slate-50"
                    : "border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50/20 shadow-sm hover:shadow-md"
                }`}
                onClick={() => !isSystemDataLoading && fileInputRef.current?.click()}
              >
                <input
                  type="file"
                  accept=".xlsx, .xls"
                  className="hidden"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  disabled={isSystemDataLoading}
                />

                <div
                  className={`p-6 rounded-full mb-6 transition-all duration-500 ${
                    isSystemDataLoading ? "bg-slate-100" : "bg-indigo-50"
                  }`}
                >
                  {isSystemDataLoading ? (
                    <RefreshCw className="animate-spin text-slate-400" size={40} />
                  ) : (
                    <FileSpreadsheet className="text-indigo-600" size={40} />
                  )}
                </div>

                <h2 className="text-xl font-bold text-slate-800 mb-2">
                  {isSystemDataLoading
                    ? "Syncing Existing Vendors..."
                    : "Upload Vendor Excel File"}
                </h2>
                <p className="text-slate-500 text-sm max-w-md mb-8 leading-relaxed">
                  {isSystemDataLoading
                    ? "Loading current vendors and terms to detect new, modified, and unchanged records..."
                    : "Upload your Vendor Excel file to begin bulk insertion and updating."}
                </p>

                <button
                  disabled={isSystemDataLoading}
                  className="px-8 py-3 bg-indigo-600 disabled:bg-slate-300 text-white font-semibold rounded-xl shadow-lg hover:bg-indigo-700 hover:shadow-indigo-200/50 transition-all transform active:scale-95"
                >
                  Select Excel File
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: REVIEW TABLE */}
          {step === 2 && (
            <div className="absolute inset-0 flex flex-col animate-in slide-in-from-right-8 duration-300">
              {validationErrors.length > 0 && (
                <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-xl flex gap-3 shadow-sm shrink-0">
                  <FileWarning className="text-red-600 mt-0.5 shrink-0" size={18} />
                  <div className="flex-1 overflow-hidden">
                    <h4 className="font-bold text-red-900 text-sm">
                      Validation Issues ({validationErrors.length})
                    </h4>
                    <ul className="list-disc list-inside text-xs text-red-700 mt-1 max-h-20 overflow-y-auto">
                      {validationErrors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              <div className="flex-1 overflow-hidden p-6">
                <div className="bg-white border border-slate-200 rounded-xl shadow-sm h-full flex flex-col">
                  {/* --- Table Toolbar with Filter Dropdown --- */}
                  <div className="p-3 px-6 border-b border-slate-200 flex flex-wrap justify-between items-center gap-3 bg-white">
                    <div className="flex items-center gap-2">
                      <Building2 size={16} className="text-slate-500" />
                      <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Review List
                      </span>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                        Showing {filteredData.length} of {fileData.length}
                      </span>
                    </div>

                    {/* Filter Dropdown */}
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-semibold text-slate-500 flex items-center gap-1.5">
                        <Filter size={13} className="text-slate-400" />
                        Status Filter:
                      </label>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors cursor-pointer"
                      >
                        <option value="ALL">All ({fileData.length})</option>
                        <option value="NEW">New ({countNew})</option>
                        <option value="UPDATED">Updated ({countUpdated})</option>
                        <option value="UNCHANGED">Unchanged ({countUnchanged})</option>
                      </select>
                    </div>
                  </div>

                  {/* --- Table Content --- */}
                  <div className="flex-1 overflow-auto">
                    <table className="w-full text-left border-collapse">
                      <thead className="bg-slate-50 sticky top-0 z-20 shadow-sm text-xs font-bold text-slate-500 uppercase tracking-wider">
                        <tr>
                          <th className="p-4 border-b border-slate-200 sticky left-0 z-30 bg-slate-50 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.1)]">
                            Vendor Details
                          </th>
                          <th className="p-4 border-b border-slate-200">
                            Contact / Address
                          </th>
                          <th className="p-4 border-b border-slate-200 text-center">
                            TIN & Tax
                          </th>
                          <th className="p-4 border-b border-slate-200">
                            Specialization
                          </th>
                          <th className="p-4 border-b border-slate-200 text-center">
                            Price & Terms
                          </th>
                          <th className="p-4 border-b border-slate-200 text-center">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-sm">
                        {filteredData.length > 0 ? (
                          filteredData.map((row, i) => (
                            <tr
                              key={i}
                              className={`transition-colors ${
                                row.status === "NEW"
                                  ? "bg-green-50/20 hover:bg-green-50/40"
                                  : row.status === "UPDATED"
                                  ? "bg-amber-50/20 hover:bg-amber-50/40"
                                  : "hover:bg-slate-50/80"
                              }`}
                            >
                              {/* Vendor Identifier */}
                              <td className="p-4 border-r border-slate-100 sticky left-0 z-10 bg-inherit shadow-[4px_0_8px_-4px_rgba(0,0,0,0.05)]">
                                <div className="flex flex-col">
                                  <span className="font-bold text-slate-800">
                                    {row.vendorName}
                                  </span>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded border border-slate-200 font-mono font-bold">
                                      {row.vendorCode}
                                    </span>
                                    <span className="text-[10px] text-slate-500 bg-slate-50 px-1 py-0.5 rounded border border-slate-200">
                                      {row.vendorStatus}
                                    </span>
                                  </div>
                                </div>
                              </td>

                              {/* Contact & Address */}
                              <td className="p-4 border-r border-slate-100 max-w-[260px]">
                                <div className="flex flex-col gap-1 text-xs text-slate-600">
                                  {row.contactPerson && (
                                    <span className="flex items-center gap-1.5 font-medium text-slate-700">
                                      <User size={12} className="text-slate-400 shrink-0" />
                                      {row.contactPerson}
                                    </span>
                                  )}
                                  {row.address && (
                                    <span className="flex items-center gap-1.5 truncate" title={row.address}>
                                      <MapPin size={12} className="text-slate-400 shrink-0" />
                                      {row.address}
                                    </span>
                                  )}
                                  {row.email && (
                                    <span className="flex items-center gap-1.5 truncate" title={row.email}>
                                      <Mail size={12} className="text-slate-400 shrink-0" />
                                      {row.email}
                                    </span>
                                  )}
                                  {row.phoneNumber && (
                                    <span className="flex items-center gap-1.5">
                                      <Phone size={12} className="text-slate-400 shrink-0" />
                                      {row.phoneNumber}
                                    </span>
                                  )}
                                </div>
                              </td>

                              {/* TIN, Non-Trade & EWT */}
                              <td className="p-4 border-r border-slate-100 text-center text-xs">
                                <div className="font-mono text-slate-700 font-semibold">
                                  {row.tin || "-"}
                                </div>
                                <div className="mt-1 flex items-center justify-center gap-1.5">
                                  {row.nonTrade && (
                                    <span className="px-1.5 py-0.5 text-[9px] font-semibold bg-purple-50 text-purple-700 border border-purple-200 rounded">
                                      Non-Trade
                                    </span>
                                  )}
                                  {row.ewt ? (
                                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded">
                                      <ShieldCheck size={9} /> EWT
                                    </span>
                                  ) : (
                                    <span className="text-[10px] text-slate-400">No EWT</span>
                                  )}
                                </div>
                              </td>

                              {/* Specialization */}
                              <td className="p-4 border-r border-slate-100 text-xs max-w-[180px]">
                                {row.specialization ? (
                                  <span className="inline-flex items-center gap-1 text-slate-600">
                                    <Briefcase size={12} className="text-slate-400 shrink-0" />
                                    {row.specialization}
                                  </span>
                                ) : (
                                  <span className="text-slate-300 italic text-[11px]">-</span>
                                )}
                              </td>

                              {/* Price Type & Terms */}
                              <td className="p-4 border-r border-slate-100 text-center text-xs">
                                <div className="font-semibold text-slate-700">
                                  {row.priceTypeDisplay || row.priceType}
                                </div>
                                <div className="text-[11px] text-slate-500 mt-0.5">
                                  {row.termsDescription || "No Terms"}
                                </div>
                              </td>

                              {/* Status: NEW, UPDATED, UNCHANGED */}
                              <td className="p-4 text-center">
                                {row.status === "NEW" && (
                                  <span className="inline-block px-2.5 py-1 text-[10px] font-bold bg-green-100 text-green-800 rounded-full border border-green-200 shadow-sm">
                                    + NEW
                                  </span>
                                )}
                                {row.status === "UPDATED" && (
                                  <span className="inline-block px-2.5 py-1 text-[10px] font-bold bg-amber-100 text-amber-800 rounded-full border border-amber-200 shadow-sm">
                                    UPDATED
                                  </span>
                                )}
                                {row.status === "UNCHANGED" && (
                                  <span className="inline-block px-2.5 py-1 text-[10px] font-semibold bg-slate-100 text-slate-600 rounded-full border border-slate-200">
                                    UNCHANGED
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={6} className="p-12 text-center text-slate-400">
                              <p className="text-sm font-semibold">
                                No vendor records found matching "{statusFilter}".
                              </p>
                              <button
                                onClick={() => setStatusFilter("ALL")}
                                className="mt-2 text-xs font-semibold text-indigo-600 hover:underline"
                              >
                                Reset filter to All
                              </button>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Summary Bar */}
                  <div className="bg-slate-50 p-3 border-t border-slate-200 flex justify-between items-center text-xs text-slate-500 px-6">
                    <span>
                      Total Vendors in File: <strong>{fileData.length}</strong>
                    </span>
                    <div className="flex gap-4">
                      <span className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-green-500" /> New:{" "}
                        <strong className="text-slate-700">{countNew}</strong>
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Updated:{" "}
                        <strong className="text-slate-700">{countUpdated}</strong>
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-slate-400" /> Unchanged:{" "}
                        <strong className="text-slate-700">{countUnchanged}</strong>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Action Bar */}
              <div className="p-4 bg-white border-t border-slate-200 flex justify-between items-center z-20">
                <button
                  onClick={() => setStep(1)}
                  className="px-5 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors flex items-center gap-2"
                >
                  <ArrowLeft size={16} /> Re-upload
                </button>
                <button
                  onClick={() => validationErrors.length === 0 && setStep(3)}
                  disabled={validationErrors.length > 0}
                  className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none text-white px-8 py-2.5 rounded-xl font-bold text-sm shadow-lg shadow-indigo-200 transition-all transform active:scale-95"
                >
                  Next: Finalize <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: CONFIRM & SAVE */}
          {step === 3 && (
            <div className="absolute inset-0 bg-slate-50 flex flex-col items-center justify-center p-6 transition-transform duration-500 ease-in-out z-30">
              <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-white text-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm border border-indigo-50">
                    <Save size={32} />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-800">
                    Confirm Vendor Import
                  </h2>
                  <p className="text-slate-500 mt-2 text-sm">
                    You are reviewing <strong>{fileData.length} vendor records</strong>.
                  </p>
                </div>

                <div className="space-y-4">
                  {/* Status Breakdown Card */}
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 grid grid-cols-3 divide-x divide-slate-200 text-center">
                    <div>
                      <div className="text-xs text-slate-400 font-semibold uppercase">
                        New
                      </div>
                      <div className="text-xl font-bold text-green-600 mt-0.5">
                        {countNew}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 font-semibold uppercase">
                        Updated
                      </div>
                      <div className="text-xl font-bold text-amber-600 mt-0.5">
                        {countUpdated}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 font-semibold uppercase">
                        Unchanged
                      </div>
                      <div className="text-xl font-bold text-slate-500 mt-0.5">
                        {countUnchanged}
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-end mb-1 ml-1">
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
                        Upload Note / Remarks (Optional)
                      </label>
                      <span className="text-[10px] font-bold text-slate-400">
                        {note.length}/50
                      </span>
                    </div>
                    <input
                      type="text"
                      value={note}
                      maxLength={50}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="e.g. Q4 Batch Vendor Sync"
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all text-slate-700"
                    />
                  </div>
                </div>

                <div className="mt-8 flex flex-col gap-3">
                  <button
                    onClick={handleFinalSave}
                    disabled={isProcessing}
                    className="w-full flex justify-center items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white py-3.5 rounded-xl font-bold text-sm shadow-lg shadow-indigo-200 hover:shadow-xl transition-all active:scale-[0.98]"
                  >
                    {isProcessing ? (
                      <RefreshCw className="animate-spin" size={20} />
                    ) : (
                      <Check size={20} />
                    )}
                    {isProcessing ? "Saving Vendors..." : "Confirm & Import Vendors"}
                  </button>
                  <button
                    onClick={() => setStep(2)}
                    disabled={isProcessing}
                    className="w-full py-2.5 text-slate-500 hover:text-slate-800 font-semibold text-sm transition-colors"
                  >
                    Back to Review
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AllVendors;
